import {useQuery} from '@tanstack/react-query';
import {useApiToken} from '@/matic/hooks/useApiClient';
import {useAppContext} from '@/matic/providers/AppProvider';
import {getQuoteEngineApi, type QuotingWorkspace} from '../api/engine-api';
import {quotingKeys} from '../query-keys';

export type QuotingAvailability = {
  loading: boolean;
  enabled: boolean;
  /** Which workspace renders quotes; null while loading or when quoting is off. */
  workspace: QuotingWorkspace | null;
};

/**
 * Whether this tenant quotes, and through which workspace — the runtime gate for
 * `/quotes`, the sidebar entry, Relay's "Quote this thread" and the AES shell. It reads
 * `GET /quoting/capabilities`; a failed read counts as "not enabled" rather than leaving
 * surfaces stuck loading.
 */
export function useQuotingCapabilities() {
  const token = useApiToken();
  const {tenantId} = useAppContext();
  return useQuery({
    queryKey: quotingKeys.engineCapabilities(tenantId ?? ''),
    queryFn: async () => getQuoteEngineApi(token).capabilities(),
    enabled: Boolean(token) && Boolean(tenantId),
    staleTime: 5 * 60_000,
    retry: 1,
  });
}

export function useQuotingAvailability(): QuotingAvailability {
  const {data, isPending, isError} = useQuotingCapabilities();
  if (isError) return {loading: false, enabled: false, workspace: null};
  if (isPending) return {loading: true, enabled: false, workspace: null};
  if (!data.enabled) return {loading: false, enabled: false, workspace: null};
  return {loading: false, enabled: true, workspace: data.workspace};
}

/**
 * The AES Quoting shell, for a tenant that quotes through the AES Quote Desk: Quotes is the
 * landing page and the rail's first entry, and the surfaces a quoting team never opens (Home,
 * Procurement, the QMS view) are hidden. The tenant's engine decides it (`workspace:
 * 'aes-desk'`), not the build, so one deployment serves AES and every other tenant.
 *
 * `undefined` until the tenant's capabilities are known: a shell waits rather than flash the
 * wrong one. Temporary split: AES keeps its own shell until it's consolidated onto the
 * QuotingSidebar and QuoteWorkbench the other quoting tenants use.
 */
export function useQuotesFirst(): boolean | undefined {
  const {loading, workspace} = useQuotingAvailability();
  return loading ? undefined : workspace === 'aes-desk';
}
